import {selectDefined} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {DataPolicy, Policy, PolicyStatement} from 'auth/PolicyConstants';
import {ActionItemType} from 'auth/settings/getActionRowDatas';
import {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isUser} from 'constants/UserConstants';
import {OrganizationPermissionRole, TeamPermissionRole} from 'orgs/OrganizationConstants';
import {Party} from 'parties/PartyConstants';

import {
  ActorCollaborator,
  CollaborationCreateOrUpdate,
  Collaborator,
  DataPolicyActorCollaborator,
  DataPolicyGroupCollaborator,
  GroupCollaborator,
  isActorCollaborator,
  isDataPolicyActorCollaborator,
  isDataPolicyGroupCollaborator,
  isSchemaPolicyActorCollaborator,
  isSchemaPolicyGroupCollaborator,
  Owner,
  PolicyType,
  SchemaPolicyActorCollaborator,
  SchemaPolicyGroupCollaborator,
} from './PermissionConstants';

// Future/ideal design: use generic User and Group Collaborator interfaces with a single policyId property and
// get rid of these functions
export function getCollaboratorPolicyKey(collaborator: ActorCollaborator): string {
  if (isDataPolicyActorCollaborator(collaborator)) {
    return 'dataPolicyId';
  }
  assert(isSchemaPolicyActorCollaborator(collaborator), 'Unexpected policy type');
  return 'schemaPolicyId';
}

export function getCollaboratorAdminPolicyKey(collaborator: GroupCollaborator): string {
  if (isDataPolicyGroupCollaborator(collaborator)) {
    return 'adminDataPolicyId';
  }
  assert(isSchemaPolicyGroupCollaborator(collaborator), 'Unexpected policy type');
  return 'adminSchemaPolicyId';
}

export function getCollaboratorMemberPolicyKey(collaborator: GroupCollaborator): string {
  if (isDataPolicyGroupCollaborator(collaborator)) {
    return 'memberDataPolicyId';
  }
  return 'memberSchemaPolicyId';
}

export function getCollaboratorPolicyId(collaborator: ActorCollaborator): string | null {
  if (isDataPolicyActorCollaborator(collaborator)) {
    return collaborator.dataPolicyId;
  }
  return collaborator.schemaPolicyId;
}

export function setCollaboratorPolicyId(
  collaborator: Partial<ActorCollaborator>,
  policyType: PolicyType,
  id: string
): void {
  if (policyType === PolicyType.DATA) {
    (collaborator as DataPolicyActorCollaborator).dataPolicyId = id;
  } else {
    (collaborator as SchemaPolicyActorCollaborator).schemaPolicyId = id;
  }
}

export function getCollaboratorMemberPolicyId(collaborator: GroupCollaborator): string | null {
  if (isDataPolicyGroupCollaborator(collaborator)) {
    return collaborator.memberDataPolicyId;
  }
  return collaborator.memberSchemaPolicyId;
}

export function setCollaboratorMemberPolicyId(
  collaborator: Partial<GroupCollaborator>,
  policyType: PolicyType,
  id: string | null
): void {
  if (policyType === PolicyType.DATA) {
    (collaborator as DataPolicyGroupCollaborator).memberDataPolicyId = id;
  } else {
    (collaborator as SchemaPolicyGroupCollaborator).memberSchemaPolicyId = id;
  }
}

export function getCollaboratorAdminPolicyId(collaborator: GroupCollaborator): string | null {
  if (isDataPolicyGroupCollaborator(collaborator)) {
    return collaborator.adminDataPolicyId;
  }
  return collaborator.adminSchemaPolicyId;
}

export function setCollaboratorAdminPolicyId(
  collaborator: Partial<GroupCollaborator>,
  policyType: PolicyType,
  id: string | null
): void {
  if (policyType === PolicyType.DATA) {
    (collaborator as DataPolicyGroupCollaborator).adminDataPolicyId = id;
  } else {
    (collaborator as SchemaPolicyGroupCollaborator).adminSchemaPolicyId = id;
  }
}

export function findMatchingPolicyStatement(
  policyStatements: Array<PolicyStatement>,
  actionItemType: ActionItemType
): PolicyStatement | undefined {
  return policyStatements.find(
    (policyStatement) =>
      actionItemType.action === policyStatement.action && actionItemType.itemType === policyStatement.itemType
  );
}

export function convertToCollaborationCreateOrUpdates(
  collaborations: Array<Collaboration>
): Array<CollaborationCreateOrUpdate> {
  return collaborations.map((c: Collaboration) => ({
    collaborator: {
      partyId: c.party.id,
      partyRole:
        (c.organizationRole as OrganizationPermissionRole) ?? (c.teamRole as TeamPermissionRole) ?? undefined,
    },
    policyId: c.policy.id,
    auditor: c.auditor ?? false,
    fromOwnership: c.fromOwnership ?? false,
  }));
}

export function isPartyAdmin(owner: Party | Owner): boolean {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'admin' in owner ? (owner.admin ?? false) : false;
}

export function convertOwnerToCollaboration(
  owner: Owner,
  policy: DataPolicy,
  role: OrganizationPermissionRole = OrganizationPermissionRole.ADMIN
): Collaboration {
  const isUserOwner = isUser(owner);
  return {
    __typename: 'CollaborationNode',
    party: isUserOwner
      ? {
          __typename: 'UserNode',
          id: owner.id,
          name: owner.name,
          handle: owner.handle,
          avatarUrl: owner.avatar_url,
        }
      : {
          __typename: 'OrganizationNode',
          id: owner.id,
          name: owner.name,
          handle: owner.handle,
          avatarUrl: owner.avatar_url,
          admin: isPartyAdmin(owner),
        },
    policy: {
      __typename: 'DataPolicyNode',
      id: policy.id,
      name: policy.name,
      isHidden: policy.isHidden,
    },
    organizationRole: isUserOwner ? null : role,
    teamRole: null,
    auditor: isUserOwner ? true : null,
    fromOwnership: true,
    resource: null,
  };
}

export function convertCollaboratorToCollaborations(
  collaborator: Collaborator,
  policies: Array<Policy>
): Array<Collaboration> {
  const isUser = isActorCollaborator(collaborator);
  if (isUser) {
    const policy = policies.find(
      (policy: Policy): boolean => policy.id === getCollaboratorPolicyId(collaborator)
    );
    return [
      {
        __typename: 'CollaborationNode',
        party: {
          __typename: collaborator.type === ModelTypeConstants.USER ? 'UserNode' : 'BenchlingAppNode',
          id: collaborator.id,
          name: collaborator.name,
          handle: collaborator.handle,
          avatarUrl: collaborator.avatar_url ?? null,
        },
        policy: {
          __typename: isDataPolicyActorCollaborator(collaborator) ? 'DataPolicyNode' : 'SchemaPolicyNode',
          id: policy!.id,
          name: policy!.name,
          isHidden: policy!.isHidden,
        },
        organizationRole: null,
        teamRole: null,
        auditor: collaborator.auditor ?? null,
        fromOwnership: collaborator.owner ?? false,
        resource: null,
      },
    ];
  }

  const memberPolicy = policies.find(
    (policy: Policy): boolean => policy.id === getCollaboratorMemberPolicyId(collaborator)
  );
  const adminPolicy = policies.find(
    (policy: Policy): boolean => policy.id === getCollaboratorAdminPolicyId(collaborator)
  );
  const collaborations: Array<Collaboration> = [];
  if (adminPolicy != null) {
    collaborations.push({
      __typename: 'CollaborationNode',
      party: {
        __typename: collaborator.type === ModelTypeConstants.ORGANIZATION ? 'OrganizationNode' : 'TeamNode',
        id: collaborator.id,
        name: collaborator.name,
        handle: collaborator.handle,
        avatarUrl: collaborator.avatar_url ?? null,
        admin: false,
      },
      policy: {
        __typename: isDataPolicyGroupCollaborator(collaborator) ? 'DataPolicyNode' : 'SchemaPolicyNode',
        id: adminPolicy.id,
        name: adminPolicy.name,
        isHidden: adminPolicy.isHidden,
      },
      organizationRole:
        collaborator.type === ModelTypeConstants.ORGANIZATION ? OrganizationPermissionRole.ADMIN : null,
      teamRole: collaborator.type === ModelTypeConstants.TEAM ? TeamPermissionRole.ADMIN : null,
      auditor: collaborator.auditor ?? null,
      fromOwnership: collaborator.owner ?? false,
      resource: null,
    });
  }
  if (memberPolicy != null) {
    collaborations.push({
      __typename: 'CollaborationNode',
      party: {
        __typename: collaborator.type === ModelTypeConstants.ORGANIZATION ? 'OrganizationNode' : 'TeamNode',
        id: collaborator.id,
        name: collaborator.name,
        handle: collaborator.handle,
        avatarUrl: collaborator.avatar_url ?? null,
        admin: false,
      },
      policy: {
        __typename: isDataPolicyGroupCollaborator(collaborator) ? 'DataPolicyNode' : 'SchemaPolicyNode',
        id: memberPolicy.id,
        name: memberPolicy.name,
        isHidden: memberPolicy.isHidden,
      },
      organizationRole:
        collaborator.type === ModelTypeConstants.ORGANIZATION ? OrganizationPermissionRole.MEMBER : null,
      teamRole: collaborator.type === ModelTypeConstants.TEAM ? TeamPermissionRole.MEMBER : null,
      auditor: collaborator.auditor ?? null,
      fromOwnership: collaborator.owner ?? false,
      resource: null,
    });
  }
  return collaborations;
}

export function convertCollaborationsToCollaborators(
  collaborations: Array<Collaboration>
): Array<Collaborator> {
  const policyType = collaborations.some(
    (collaboration) => collaboration.policy.__typename === 'DataPolicyNode'
  )
    ? PolicyType.DATA
    : PolicyType.SCHEMA;
  const collaborators: Array<Collaborator> = [];
  collaborations.forEach((collaboration: Collaboration) => {
    const role = collaboration.teamRole ?? collaboration.organizationRole;
    if (role == null) {
      const collaborator: Partial<ActorCollaborator> = selectDefined({
        id: collaboration.party.id,
        handle: collaboration.party.handle,
        name: collaboration.party.name,
        type:
          collaboration.party.__typename === 'UserNode'
            ? ModelTypeConstants.USER
            : ModelTypeConstants.BENCHLING_APP,
        owner: collaboration.fromOwnership ?? undefined,
        avatar_url: collaboration.party.avatarUrl ?? undefined,
      });
      setCollaboratorPolicyId(collaborator, policyType, collaboration.policy.id);
      collaborators.push(collaborator as Collaborator);
    } else {
      let collaborator: Partial<GroupCollaborator> = collaborators.find(
        (collaborator: Collaborator) => collaborator.id === collaboration.party.id
      ) as SchemaPolicyGroupCollaborator;
      if (collaborator === undefined) {
        collaborator = selectDefined({
          id: collaboration.party.id,
          handle: collaboration.party.handle,
          name: collaboration.party.name,
          type:
            collaboration.party.__typename === 'OrganizationNode'
              ? ModelTypeConstants.ORGANIZATION
              : ModelTypeConstants.TEAM,
          owner: collaboration.fromOwnership ?? undefined,
          avatar_url: collaboration.party.avatarUrl ?? undefined,
        });
        setCollaboratorAdminPolicyId(collaborator, policyType, null);
        setCollaboratorMemberPolicyId(collaborator, policyType, null);
        collaborators.push(collaborator as GroupCollaborator);
      }
      if (role === OrganizationPermissionRole.ADMIN) {
        setCollaboratorAdminPolicyId(collaborator, policyType, collaboration.policy.id);
      } else {
        setCollaboratorMemberPolicyId(collaborator, policyType, collaboration.policy.id);
      }
    }
  });
  return collaborators;
}
